import httpClient from '../httpClient';

// ===== INTERFACES =====

export interface Employee {
  rank: number;
  name: string;
  initials: string;
  level: string;
  levelColor: string;
  progress: number;
  progressText: string;
  points: string;
  coursesCompleted: number;
  certificates: number;
  trend: 'up' | 'down' | 'same';
}

export interface ReportCard {
  id: string;
  title: string;
  description: string;
  icon: string;
  iconColor: string;
  lastGenerated: string;
  formats: string[];
}

export interface DepartmentPerformance {
  department: string;
  completedPercentage: number;
  totalPercentage: number;
  totalUsers?: number;
  usersWithEnrollments?: number;
}

export interface SkillScore {
  skill: string;
  score: number; // 0-100
  usersAssessed?: number;
}

export interface CompletionTrendData {
  month: string;
  completed: number;
  enrolled: number;
  target: number;
}

export interface CertificationDistribution {
  type: string;
  percentage: number;
  color: string;
}

export interface GenerateReportParams {
  reportType: 'user' | 'course-completion' | 'learning-engagement' | 'skills-assessment';
  format: 'pdf' | 'excel' | 'csv';
  dateRange?: {
    from: string;
    to: string;
  };
  department?: string;
}

export interface GenerateReportResponse {
  fileName: string;
  format: string;
  size: number;
  recordCount: number;
  downloadUrl: string;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

// ===== API ENDPOINTS =====
const ENDPOINTS = {
  LEADERBOARD: '/admin/reports/leaderboard',
  DEPARTMENT_PERFORMANCE: '/admin/reports/department-performance',
  COMPLETION_TRENDS: '/admin/reports/completion-trends',
  CERTIFICATION_DISTRIBUTION: '/admin/reports/certification-distribution',
  SKILLS_ASSESSMENT: '/admin/reports/skills-assessment',
  GENERATE_REPORT: '/admin/reports/generate',
  DOWNLOAD_REPORT: '/admin/reports/download',
};

// ===== SERVICE FUNCTIONS =====

export const getLeaderboardData = async (limit: number = 50): Promise<{
  success: boolean;
  data?: Employee[];
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.get<ApiResponse<Employee[]>>(
      `${ENDPOINTS.LEADERBOARD}?limit=${limit}`
    );

    return {
      success: true,
      data: response.data || [],
      error: null
    };
  } catch (error: any) {
    console.error('ReportAnalyticsService: Error fetching leaderboard:', error);
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch leaderboard data' }
    };
  }
};

export const getReportCards = async (): Promise<{
  success: boolean;
  data?: ReportCard[];
  error?: { message: string } | null;
}> => {
  try {
    // Report cards are static configuration, no API needed
    const response: ReportCard[] = [
      {
        id: 'learning-engagement',
        title: 'Learning Engagement Report',
        description: 'Detailed analysis of user engagement and activity',
        icon: 'TrendingUp',
        iconColor: 'blue',
        lastGenerated: 'Available',
        formats: ['PDF', 'EXCEL', 'CSV']
      },
      {
        id: 'course-completion',
        title: 'Course Completion Report',
        description: 'Track completion rates across all courses',
        icon: 'Award',
        iconColor: 'green',
        lastGenerated: 'Available',
        formats: ['PDF', 'EXCEL', 'CSV']
      },
      {
        id: 'skills-assessment',
        title: 'Skills Assessment Report',
        description: 'Analyze skill development and competency levels',
        icon: 'BarChart3',
        iconColor: 'purple',
        lastGenerated: 'Available',
        formats: ['PDF', 'EXCEL', 'CSV']
      },
      {
        id: 'user',
        title: 'User Report',
        description: 'Overview of users with certifications and achievements',
        icon: 'Users',
        iconColor: 'orange',
        lastGenerated: 'Available',
        formats: ['PDF', 'EXCEL', 'CSV']
      }
    ];

    return {
      success: true,
      data: response,
      error: null
    };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch report cards' }
    };
  }
};

export const getDepartmentPerformance = async (): Promise<{
  success: boolean;
  data?: DepartmentPerformance[];
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.get<ApiResponse<DepartmentPerformance[]>>(
      ENDPOINTS.DEPARTMENT_PERFORMANCE
    );

    return {
      success: true,
      data: response.data || [],
      error: null
    };
  } catch (error: any) {
    console.error('ReportAnalyticsService: Error fetching department performance:', error);
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch department performance' }
    };
  }
};

export const getSkillsAssessment = async (): Promise<{
  success: boolean;
  data?: SkillScore[];
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.get<ApiResponse<SkillScore[]>>(
      ENDPOINTS.SKILLS_ASSESSMENT
    );

    return {
      success: true,
      data: response.data || [],
      error: null
    };
  } catch (error: any) {
    console.error('ReportAnalyticsService: Error fetching skills assessment:', error);
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch skills assessment' }
    };
  }
};

export const getCompletionTrends = async (): Promise<{
  success: boolean;
  data?: CompletionTrendData[];
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.get<ApiResponse<CompletionTrendData[]>>(
      ENDPOINTS.COMPLETION_TRENDS
    );

    return {
      success: true,
      data: response.data || [],
      error: null
    };
  } catch (error: any) {
    console.error('ReportAnalyticsService: Error fetching completion trends:', error);
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch completion trends' }
    };
  }
};

export const getCertificationDistribution = async (): Promise<{
  success: boolean;
  data?: CertificationDistribution[];
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.get<ApiResponse<CertificationDistribution[]>>(
      ENDPOINTS.CERTIFICATION_DISTRIBUTION
    );

    return {
      success: true,
      data: response.data || [],
      error: null
    };
  } catch (error: any) {
    console.error('ReportAnalyticsService: Error fetching certification distribution:', error);
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch certification distribution' }
    };
  }
};

export const generateReport = async (params: GenerateReportParams): Promise<{
  success: boolean;
  data?: GenerateReportResponse;
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.post<ApiResponse<GenerateReportResponse>>(
      ENDPOINTS.GENERATE_REPORT,
      params
    );

    return {
      success: true,
      data: response.data,
      error: null
    };
  } catch (error: any) {
    console.error('ReportAnalyticsService: Error generating report:', error);
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to generate report' }
    };
  }
};

export const downloadReport = async (fileName: string): Promise<Blob | null> => {
  try {
    const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api';
    const response = await fetch(
      `${baseUrl}${ENDPOINTS.DOWNLOAD_REPORT}/${fileName}`,
      {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken') || ''}`
        }
      }
    );

    if (!response.ok) {
      throw new Error('Failed to download report');
    }

    return await response.blob();
  } catch (error: any) {
    console.error('ReportAnalyticsService: Error downloading report:', error);
    return null;
  }
};
